/**
 * Global Popup System
 * Reusable popup functionality for the entire theme
 * Usage: Initialize with data attributes and call showPopup() method
 */

class GlobalPopup {
  constructor() {
    this.popupOverlay = null;
    this.popupClose = null;
    this.popupLogo = null;
    this.popupTitle = null;
    this.popupDescription = null;
    this.isInitialized = false;
    
    this.init();
  }

  init() {
    if (this.isInitialized) return;
    
    // Create popup HTML if it doesn't exist
    this.createPopupHTML();
    
    // Get popup elements
    this.popupOverlay = document.querySelector('.popup-overlay');
    this.popupClose = document.querySelector('.popup-close');
    this.popupLogo = document.querySelector('.popup-logo-image');
    this.popupTitle = document.querySelector('.popup-title');
    this.popupDescription = document.querySelector('.popup-description');
    
    if (!this.popupOverlay) {
      console.error('GlobalPopup: Popup overlay not found');
      return;
    }
    
    // Bind events
    this.bindEvents();
    
    this.isInitialized = true;
  }

  createPopupHTML() {
    // Check if popup already exists
    if (document.querySelector('.popup-overlay')) return;
    
    const popupHTML = `
      <div class="popup-overlay" data-popup-overlay>
        <div class="popup-container">
          <div class="popup-content">
            <!-- Close Button -->
            <div class="popup-header">
              <button class="popup-close" data-popup-close aria-label="Close popup">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.99414 8.99921L22.9949 22.9999" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/>
                  <path d="M8.99414 23.0008L22.9949 9.00008" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/>
                </svg>

              </button>
            </div>
            
            <!-- Popup Body -->
            <div class="popup-body">
              <!-- Logo -->
              <div class="popup-logo">
                <img 
                  src="" 
                  alt=""
                  class="popup-logo-image"
                  data-popup-logo
                >
              </div>
              
              <!-- Title -->
              <h3 class="popup-title" data-popup-title></h3>
              
              <!-- Description -->
              <div class="popup-description" data-popup-description></div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // Append to body
    document.body.insertAdjacentHTML('beforeend', popupHTML);
  }

  bindEvents() {
    if (!this.popupClose) return;
    
    // Close button
    this.popupClose.addEventListener('click', () => this.closePopup());
    
    // Overlay click
    this.popupOverlay.addEventListener('click', (e) => {
      if (e.target === this.popupOverlay) {
        this.closePopup();
      }
    });
    
    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popupOverlay.classList.contains('popup-overlay--active')) {
        this.closePopup();
      }
    });
  }

  showPopup(data) {
    if (!this.isInitialized) {
      this.init();
    }
    
    if (!this.popupOverlay) {
      console.error('GlobalPopup: Cannot show popup - overlay not found');
      return;
    }
    
    // Update popup content
    if (this.popupLogo && data.logo) {
      this.popupLogo.src = data.logo;
      this.popupLogo.alt = data.title || '';
      // Show logo section
      this.popupLogo.closest('.popup-logo').style.display = 'flex';
    } else if (this.popupLogo) {
      // Hide logo section when no logo provided
      this.popupLogo.closest('.popup-logo').style.display = 'none';
    }
    
    if (this.popupTitle && data.title) {
      this.popupTitle.innerHTML = data.title;
    }
    
    if (this.popupDescription && data.description) {
      this.popupDescription.innerHTML = data.description;
    }
    
    // Show popup
    this.popupOverlay.classList.add('popup-overlay--active');
    document.body.classList.add('popup-open');
  }

  closePopup() {
    if (!this.popupOverlay) return;
    
    this.popupOverlay.classList.remove('popup-overlay--active');
    document.body.classList.remove('popup-open');
  }

  // Static method for easy usage
  static show(data) {
    if (!window.globalPopup) {
      window.globalPopup = new GlobalPopup();
    }
    window.globalPopup.showPopup(data);
  }

  static close() {
    if (window.globalPopup) {
      window.globalPopup.closePopup();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  window.globalPopup = new GlobalPopup();
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GlobalPopup;
}
